import {
  renderLensesOffscreenWithId,
  cleanupOffscreenContainer,
  waitForLensesReady,
} from "@/helpers/renderLensesOffscreen";
import { getHtml2canvas } from "./lazyLibs";
import { t } from "./labelHelpers";
import { usePedidosStore } from "@/stores/pedidos";
import type { LensesCaptureResult, OfertasStore, VuexLikeState } from "./types";

const EMPTY_CAPTURE: LensesCaptureResult = {
  imagenLente: "",
  canvasRealWidth: 0,
  canvasRealHeight: 0,
  textoOD: "",
  textoOI: "",
};

const HTML2CANVAS_OPTIONS = {
  backgroundColor: "#ffffff",
  scale: 1,
  useCORS: true,
  logging: false,
  allowTaint: true,
};

// Texto bajo cada lente. `TXT_LENT` es un dato de salida (solo llega calculado);
// mientras no lo haya se usa el nombre de la lente elegida en el formulario para
// no imprimir un "OD:" suelto.
function textosOdOi(pedido: any): { textoOD: string; textoOI: string } {
  const nombreFormulario = usePedidosStore().caracteristicasLente?.Nombre || "";
  const texto = (ojoOut: any) => ojoOut?.TXT_LENT || nombreFormulario;
  return {
    textoOD: `${t("od")}: ${texto(pedido?.oClaseBAPI?.ojoOutDerecho)}`,
    textoOI: `${t("oi")}: ${texto(pedido?.oClaseBAPI?.ojoOutIzquierdo)}`,
  };
}

// Captura desde el div visible "#importador-imagenes" haciendo visibles
// temporalmente los ancestros ocultos por v-show.
async function captureFromVisible(
  elementoVisible: HTMLElement,
  pedido: any,
): Promise<LensesCaptureResult> {
  const html2canvas = await getHtml2canvas();

  const hiddenAncestors: { el: HTMLElement; prev: string }[] = [];
  let parent = elementoVisible.parentElement;
  while (parent) {
    if (parent.style.display === "none") {
      hiddenAncestors.push({ el: parent, prev: parent.style.display });
      parent.style.display = "";
    }
    parent = parent.parentElement;
  }

  const lenseDraw1 = elementoVisible.querySelector(".lense-draw--od") as HTMLElement | null;
  const lenseDraw2 = elementoVisible.querySelector(".lense-draw--oi") as HTMLElement | null;
  const anchoLentes = (lenseDraw1?.offsetWidth || 0) + (lenseDraw2?.offsetWidth || 0) + 40;

  const tempContainer = document.createElement("div");
  tempContainer.style.cssText = `
    position: absolute; top: -9999px; left: -9999px;
    display: flex; flex-direction: row; align-items: center; justify-content: center;
    gap: 20px; padding: 10px; background: white;
    width: ${anchoLentes}px;
  `;
  if (lenseDraw1) tempContainer.appendChild(lenseDraw1.cloneNode(true));
  if (lenseDraw2) tempContainer.appendChild(lenseDraw2.cloneNode(true));
  document.body.appendChild(tempContainer);

  const canvas = await html2canvas(tempContainer, {
    ...HTML2CANVAS_OPTIONS,
    width: tempContainer.scrollWidth,
    height: tempContainer.scrollHeight,
  });

  document.body.removeChild(tempContainer);
  hiddenAncestors.forEach(({ el, prev }) => { el.style.display = prev; });

  return {
    imagenLente: canvas.toDataURL("image/png"),
    canvasRealWidth: canvas.width,
    canvasRealHeight: canvas.height,
    ...textosOdOi(pedido),
  };
}

// Renderiza los LenseDraw off-screen y los rasteriza a PNG vía SVG nativo del
// navegador. Sustituye al antiguo `html2canvas(container)` (DOM-walker en JS
// puro, 1-2 s por captura con SVGs complejos): la rasterización SVG la hace
// el motor nativo (Skia en Chrome), una orden de magnitud más rápido.
//
// Pasos: (1) recortar los dos <svg> ya renderizados por Vue, (2) renombrar
// ids para que `hback`/`rg` no colisionen entre OD y OI, (3) inline el fill
// del path principal (la regla SCSS scoped `.horizontal-gradient` se pierde
// fuera del documento), (4) montar un SVG envoltorio con ambos colocados
// según su layout real, (5) cargarlo en un <img> y volcarlo a canvas.
async function captureFromOffscreen(pedido: any): Promise<LensesCaptureResult> {
  const container = await renderLensesOffscreenWithId(pedido, undefined, true);
  try {
    await waitForLensesReady(container);

    const svgs = Array.from(container.querySelectorAll<SVGSVGElement>("svg"));
    if (svgs.length === 0) return EMPTY_CAPTURE;

    const sides = ["od", "oi"];
    const containerRect = container.getBoundingClientRect();
    const placements = svgs.map((svg, i) => {
      const side = sides[i] ?? `s${i}`;
      // Renombrar ids dentro del SVG (defs como `hback`, `rg`) para que no
      // colisionen al combinar OD + OI en un solo SVG envoltorio.
      svg.querySelectorAll<SVGElement>("[id]").forEach((el) => {
        el.id = `${el.id}-${side}`;
      });
      // El path principal usa fill via clase scoped (`fill: url(#hback)`).
      // Sin el <style> de Vue scoped, esa regla no aplica fuera del documento;
      // fijamos el fill inline apuntando al id ya renombrado.
      svg.querySelectorAll<SVGPathElement>("path.horizontal-gradient").forEach((p) => {
        p.setAttribute("fill", `url(#hback-${side})`);
      });
      const r = svg.getBoundingClientRect();
      return {
        x: Math.round(r.left - containerRect.left),
        y: Math.round(r.top - containerRect.top),
        width: Math.round(r.width),
        height: Math.round(r.height),
      };
    });

    const totalWidth = Math.max(1, Math.ceil(containerRect.width));
    const totalHeight = Math.max(1, Math.ceil(containerRect.height));
    const xmlns = "http://www.w3.org/2000/svg";
    const serializer = new XMLSerializer();

    let inner = `<rect x="0" y="0" width="${totalWidth}" height="${totalHeight}" fill="#ffffff"/>`;
    svgs.forEach((svg, i) => {
      const p = placements[i];
      const clone = svg.cloneNode(true) as SVGSVGElement;
      // Posicionamos cada <svg> hijo en el sistema de coordenadas del wrapper,
      // preservando su viewBox y dimensiones nativas.
      clone.setAttribute("x", String(p.x));
      clone.setAttribute("y", String(p.y));
      clone.setAttribute("width", String(p.width));
      clone.setAttribute("height", String(p.height));
      inner += serializer.serializeToString(clone);
    });
    const svgString =
      `<svg xmlns="${xmlns}" width="${totalWidth}" height="${totalHeight}" ` +
      `viewBox="0 0 ${totalWidth} ${totalHeight}">${inner}</svg>`;

    const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    try {
      const img = new Image();
      img.src = url;
      await img.decode();
      const canvas = document.createElement("canvas");
      canvas.width = totalWidth;
      canvas.height = totalHeight;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("canvas 2D context unavailable");
      ctx.drawImage(img, 0, 0);
      return {
        imagenLente: canvas.toDataURL("image/png"),
        canvasRealWidth: canvas.width,
        canvasRealHeight: canvas.height,
        ...textosOdOi(pedido),
      };
    } finally {
      URL.revokeObjectURL(url);
    }
  } finally {
    cleanupOffscreenContainer(container);
  }
}

export async function captureLensesImage(
  pedido: any,
  usarImagenCacheada: boolean,
  useOffscreenLensesOnly: boolean,
): Promise<LensesCaptureResult> {
  const esDuplicado = pedido?.oClaseBAPI?.isDuplicatedOrder;
  const tieneFormas = pedido?.oClaseBAPI?.FormaROD || pedido?.oClaseBAPI?.FormaROI;
  const debeGenerar = !usarImagenCacheada || tieneFormas || esDuplicado;
  if (!debeGenerar) return EMPTY_CAPTURE;

  const elementoVisible = useOffscreenLensesOnly
    ? null
    : document.getElementById("importador-imagenes");

  try {
    if (elementoVisible && tieneFormas) {
      return await captureFromVisible(elementoVisible, pedido);
    }
    return await captureFromOffscreen(pedido);
  } catch {
    return EMPTY_CAPTURE;
  }
}

// Calcula el `fit` de pdfmake para que las lentes se impriman a tamaño físico real.
// El canvas captura el contenedor entero (con padding); pdfmake escala todo,
// así que hay que sobre-escalar para compensar y dejar la lente al tamaño deseado.
export async function calcularFitDinamico(
  pedido: any,
  capture: LensesCaptureResult,
  store: { state: VuexLikeState },
  ofertasStore: OfertasStore,
): Promise<[number, number] | null> {
  try {
    const { canvasRealWidth, canvasRealHeight } = capture;
    if (canvasRealWidth === 0 || canvasRealHeight === 0) return null;

    let valorPantalla = 60;
    try {
      const ajustes = await ofertasStore.LoadInfo();
      if (ajustes && ajustes.VALOR_PANTALLA) valorPantalla = ajustes.VALOR_PANTALLA;
    } catch { /* fallback a 60 */ }

    const diamOdStr = String(
      pedido?.oClaseBAPI?.ojoOutDerecho?.DIAMETRO ?? store?.state?.pedidos?.od?.diametro ?? "60/60",
    );
    const diamOiStr = String(
      pedido?.oClaseBAPI?.ojoOutIzquierdo?.DIAMETRO ?? store?.state?.pedidos?.oi?.diametro ?? "60/60",
    );

    const parseDiam = (d: string): number => {
      const sep = d.includes("/") ? "/" : "X";
      const first = parseFloat(d.split(sep)[0]);
      return isNaN(first) ? 60 : first;
    };

    const maxDiam = Math.max(parseDiam(diamOdStr), parseDiam(diamOiStr));

    // Mismo REF_DIAMETRO que LenseDraw.calculateSvg().
    const REF_DIAMETRO = 75;
    const calculatedSizePx = (d: number) =>
      Math.round((96 * ((d - 5) * d) / valorPantalla) / 25.4);
    const svgHeight = calculatedSizePx(REF_DIAMETRO);

    // Tamaño real deseado en puntos PDF (1mm = 72/25.4 pts).
    const targetHeightPts = maxDiam * 72 / 25.4;

    // En LenseDraw: escala_path = (60/VP)^2 * (0.03*VP + 0.03). Factor px/unit = 2.
    const escalaPath = Math.pow(60 / valorPantalla, 2) * (0.03 * valorPantalla + 0.03);
    const lensToSvgRatio = (maxDiam * escalaPath * 2) / svgHeight;

    const canvasToSvgRatio = canvasRealHeight / svgHeight;
    const fitHeight = Math.round(targetHeightPts * canvasToSvgRatio / lensToSvgRatio);
    const fitWidth = Math.round(canvasRealWidth * (fitHeight / canvasRealHeight));

    return [fitWidth, fitHeight];
  } catch {
    return [520, 350]; // fallback seguro
  }
}
